/* eslint-disable formatjs/no-literal-string-in-jsx */
import React from 'react'
import { motion } from 'motion/react'
import { FormattedMessage } from 'react-intl'
import ReactMarkdown from 'react-markdown'

import { Label } from '@slite/components'
import { CircledCross, SliteAgentIcon, SuccessCircledIcon } from '@slite/icons'

import type { DocLabel, DocNode } from './content'

// ─── Markdown rendering ─────────────────────────────────────────────────

// `{+text+}` → add pill, `{-text-}` → remove pill. Rewritten to markdown links
// with a custom href so ReactMarkdown still parses surrounding markdown.
// Whole-line headings keep their heading marker outside the diff link so they
// render as rich text instead of plain "## Heading" copy.
const preprocessDiff = (markdown: string): string =>
  markdown
    .replace(/^(\{\+)(#{1,6})\s+([^{}]+?)(\+\})$/gm, '$2 [$3](diff:add)')
    .replace(/^(\{-)(#{1,6})\s+([^{}]+?)(-\})$/gm, '$2 [$3](diff:remove)')
    .replace(/\{\+([^{}]+?)\+\}/g, '[$1](diff:add)')
    .replace(/\{-([^{}]+?)-\}/g, '[$1](diff:remove)')

const docContainerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07, delayChildren: 0.04 } },
}

const docBlockVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.32, ease: [0.4, 0, 0.2, 1] as const },
  },
}

const DIFF_MARK = {
  add: { background: '#dcfce7' },
  remove: {
    background: '#fee2e2',
    textDecoration: 'line-through',
    textDecorationColor: '#991b1b',
  },
}

const DiffMark = ({
  kind,
  children,
}: {
  kind: 'add' | 'remove'
  children?: React.ReactNode
}) => (
  <span
    data-doc-diff={kind}
    style={{
      margin: 0,
      padding: 0,
      borderRadius: 4,
      color: 'inherit',
      font: 'inherit',
      lineHeight: 'inherit',
      boxDecorationBreak: 'clone',
      WebkitBoxDecorationBreak: 'clone',
      ...DIFF_MARK[kind],
    }}
  >
    {children}
  </span>
)

const animated =
  <T extends keyof React.JSX.IntrinsicElements>(tag: T, className: string) =>
  ({ children }: { children?: React.ReactNode }) => {
    const Tag = motion[tag as 'h1'] as typeof motion.h1
    return (
      <Tag
        layout="position"
        className={className}
        variants={docBlockVariants}
        transition={{ layout: { duration: 0.28, ease: [0.4, 0, 0.2, 1] } }}
      >
        {children as React.ReactNode}
      </Tag>
    )
  }

const markdownComponents = {
  a: ({ href, children }: { href?: string; children?: React.ReactNode }) => {
    if (href === 'diff:add') return <DiffMark kind="add">{children}</DiffMark>
    if (href === 'diff:remove')
      return <DiffMark kind="remove">{children}</DiffMark>
    return <a href={href}>{children}</a>
  },
  h1: animated('h1', 'shade-05'),
  h2: animated('h2', 'shade-05'),
  h3: animated('h3', 'shade-05'),
  p: animated('p', 'shade-10'),
  ul: animated('ul', 'shade-10'),
  ol: animated('ol', 'shade-10'),
}

// ─── Label + Company overview ───────────────────────────────────────────

const LabelBadge = ({ label }: { label: DocLabel }) => {
  const isVerified = label === 'verified'
  return (
    <Label
      variant={isVerified ? 'success' : 'danger'}
      icon={isVerified ? SuccessCircledIcon : CircledCross}
      iconClassName="margin-right-4"
      uppercase={false}
      className="font-size-large font-weight-semibold line-height-largest"
    >
      {isVerified ? (
        <FormattedMessage
          id="new.hero.demo.label.verified"
          defaultMessage="Verified"
        />
      ) : (
        <FormattedMessage
          id="new.hero.demo.label.outdated"
          defaultMessage="Outdated"
        />
      )}
    </Label>
  )
}

const SelfMaintainedBadge = () => (
  <Label
    variant="purple"
    icon={SliteAgentIcon}
    iconClassName="margin-right-4"
    uppercase={false}
    className="font-size-large font-weight-semibold line-height-largest"
  >
    <FormattedMessage
      id="new.hero.demo.label.selfMaintained"
      defaultMessage="Self-maintained"
    />
  </Label>
)

// ─── DocBody (main export) ──────────────────────────────────────────────

export const DocBody = ({
  current,
  animationKey,
}: {
  current: DocNode
  animationKey: number
}) => (
  <div
    className="flexbox-grow padding-vertical-32 padding-horizontal-40 overflow-auto"
    style={{ minWidth: 0, maxWidth: 650, margin: '0 auto' }}
  >
    <div className="flexbox flexbox-align-center flexbox-gap-8 flexbox-wrap margin-bottom-16">
      <LabelBadge label={current.label ?? 'verified'} />
      {current.isSelfMaintained && <SelfMaintainedBadge />}
    </div>
    {current.customRender
      ? current.customRender
      : current.content && (
          <motion.div
            key={`${current.id}:${animationKey}`}
            className="simple-rich-text"
            variants={docContainerVariants}
            initial="hidden"
            animate="visible"
          >
            <ReactMarkdown
              components={markdownComponents}
              urlTransform={(url) => url}
            >
              {preprocessDiff(current.content)}
            </ReactMarkdown>
          </motion.div>
        )}
  </div>
)
